import type {
  PartnerClient,
  PartnerClientWithSecret
} from '@epilot/sdk/identity'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { errorMessage, ScopePicker } from './shared'
import {
  clientSchema,
  ENVIRONMENTS,
  lines,
  SCOPES,
  sectorHost
} from './validation'

import { identityClient } from '@/api/identity-client'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/use-toast'
import { useRefreshIdentity } from '@/services/identity.service'

// What the inputs hold (strings, one URI per line); the API shape is derived in toInput and
// validated by the shared clientSchema so the UI and the API contract cannot drift apart.
const clientFormSchema = z.object({
  partner_key: z.string(),
  display_name: z.string(),
  environment: z.enum(ENVIRONMENTS),
  description: z.string(),
  redirect_uris: z.string(),
  scopes_allowed: z.array(z.enum(SCOPES)),
  sector_identifier_uri: z.string(),
  initiate_login_uri: z.string(),
  launch_target_prefixes: z.string(),
  allow_sector_change: z.boolean()
})

type ClientFormValues = z.infer<typeof clientFormSchema>

// The schema's input shape: the parse that follows (in onSubmit and the resolver) is what narrows
// the scope list to the API's non-empty tuple, so no cast is needed here.
const toInput = (values: ClientFormValues): z.input<typeof clientSchema> => ({
  partner_key: values.partner_key.trim(),
  display_name: values.display_name.trim(),
  environment: values.environment,
  description: values.description.trim() || undefined,
  redirect_uris: lines(values.redirect_uris) as [string, ...string[]],
  scopes_allowed: values.scopes_allowed,
  sector_identifier_uri: values.sector_identifier_uri.trim() || undefined,
  initiate_login_uri: values.initiate_login_uri.trim() || undefined,
  launch_target_prefixes: lines(values.launch_target_prefixes)
})

const SECTOR_CHANGE_MESSAGE =
  'Changing the subject identifier host changes every user’s sub at this application. Confirm the sector change before saving.'

const formSchemaFor = (client?: PartnerClient) =>
  clientFormSchema.superRefine((values, ctx) => {
    const result = clientSchema.safeParse(toInput(values))

    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: 'custom',
          path: [String(issue.path[0] ?? 'display_name')],
          message: issue.message
        })
      }

      return
    }
    if (
      (client?.status === 'active' || client?.status === 'suspended') &&
      sectorHost(client) !== sectorHost(result.data) &&
      !values.allow_sector_change
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['allow_sector_change'],
        message: SECTOR_CHANGE_MESSAGE
      })
    }
  })

export const ClientForm = ({
  client,
  onCreated
}: {
  client?: PartnerClient
  onCreated?: (client: PartnerClientWithSecret) => void
}) => {
  const refresh = useRefreshIdentity()
  const retired = client?.status === 'retired'
  const canChangeSector =
    client?.status === 'active' || client?.status === 'suspended'
  const schema = useMemo(() => formSchemaFor(client), [client])
  const form = useForm<ClientFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      partner_key: client?.partner_key ?? '',
      display_name: client?.display_name ?? '',
      environment: client?.environment ?? 'dev',
      description: client?.description ?? '',
      redirect_uris: client?.redirect_uris.join('\n') ?? '',
      scopes_allowed: client?.scopes_allowed ?? [...SCOPES],
      sector_identifier_uri: client?.sector_identifier_uri ?? '',
      initiate_login_uri: client?.initiate_login_uri ?? '',
      launch_target_prefixes: client?.launch_target_prefixes?.join('\n') ?? '',
      allow_sector_change: false
    },
    mode: 'onChange'
  })
  const busy = form.formState.isSubmitting

  const onSubmit = async (values: ClientFormValues) => {
    const input = clientSchema.parse(toInput(values))

    try {
      if (client) {
        const {
          partner_key: _partnerKey,
          environment: _environment,
          ...update
        } = input

        await identityClient.update(client.client_id, {
          ...update,
          config_version: client.config_version,
          allow_sector_change: values.allow_sector_change
        })
        toast({ title: 'Application updated' })
      } else {
        // The one-time secret goes to the caller's component state only; see SecretDialog.
        onCreated?.(await identityClient.create(input))
      }
      void refresh()
    } catch (error) {
      toast({
        title: 'Error',
        description: `Could not save the application. ${errorMessage(error)}`,
        variant: 'destructive'
      })
    }
  }

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset className="space-y-4" disabled={busy || retired}>
          <div className="grid gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Application name</FormLabel>
                  <FormControl>
                    <Input maxLength={128} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="partner_key"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Partner key</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={64}
                      {...field}
                      disabled={Boolean(client)}
                    />
                  </FormControl>
                  <FormDescription>
                    A stable key shared by this partner’s applications, e.g.
                    partner-name.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="environment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Partner environment</FormLabel>
                  <Select
                    disabled={Boolean(client) || busy || retired}
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="dev">Development</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The partner’s environment. This does not change the admin
                    portal’s stage.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Input maxLength={1024} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="redirect_uris"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Redirect URIs</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="https://app.example.com/oidc/callback"
                    rows={3}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  One exact HTTPS callback URL per line. Wildcard matching is
                  not supported.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="scopes_allowed"
            render={({ field }) => (
              <FormItem>
                <ScopePicker
                  disabled={busy || retired}
                  onChange={field.onChange}
                  value={field.value}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <details
            className="rounded-md border border-border p-4"
            open={Boolean(
              client?.sector_identifier_uri || client?.initiate_login_uri
            )}
          >
            <summary className="cursor-pointer text-sm font-medium">
              Advanced settings
            </summary>
            <div className="mt-4 space-y-4">
              <FormField
                control={form.control}
                name="sector_identifier_uri"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sector identifier URL (optional)</FormLabel>
                    <FormControl>
                      <Input type="url" {...field} />
                    </FormControl>
                    <FormDescription>
                      An HTTPS JSON document listing the redirect URIs. Required
                      when redirects use multiple hosts.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {canChangeSector && (
                <FormField
                  control={form.control}
                  name="allow_sector_change"
                  render={({ field }) => (
                    <FormItem>
                      <label
                        className="flex items-center gap-2 text-sm"
                        htmlFor="allow-sector-change"
                      >
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            disabled={busy}
                            id="allow-sector-change"
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)
                            }
                          />
                        </FormControl>
                        I understand that changing the sector changes existing
                        user subject identifiers.
                      </label>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="initiate_login_uri"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Login initiation URL (optional)</FormLabel>
                    <FormControl>
                      <Input type="url" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="launch_target_prefixes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Allowed launch target prefixes (optional)
                    </FormLabel>
                    <FormControl>
                      <Textarea rows={2} {...field} />
                    </FormControl>
                    <FormDescription>
                      One HTTPS origin and path per line. No query strings or
                      fragments.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </details>
          {!client && (
            <p className="text-sm text-muted-foreground">
              Created as a draft. Save the one-time secret, enable the approved
              organizations, then activate the application.
            </p>
          )}
          {!retired && (
            <Button loading={busy} type="submit">
              {client ? 'Save changes' : 'Create application'}
            </Button>
          )}
        </fieldset>
      </form>
    </Form>
  )
}
